import React, { useState } from 'react';
import { useDashboardKPIs } from '../../../hooks/useDashboardKPIs.js';
import UsageCalendarHeatmap from '../../analytics/UsageCalendarHeatmap';
import SessionEvolutionChart from '../../analytics/SessionEvolutionChart';
import SessionTestComponent from '../../SessionTestComponent'; // Our new test component
import './AdminAnalyticsDashboard.css';
import { FaInfoCircle, FaSlidersH } from 'react-icons/fa';
import ToolUsageChart from '../../analytics/ToolUsageChart';
import ProjectsKPI from '../../analytics/ProjectsKPI';

const AdminAnalyticsDashboard = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('7 derniers jours');
  const { sessionStats, loading, error, refreshKPIs } = useDashboardKPIs(selectedPeriod);
  const [showDetails, setShowDetails] = useState(false);
  const [durationFilter, setDurationFilter] = useState('total'); // 'total', 'active', 'inactive'
  const [showTestComponent, setShowTestComponent] = useState(false); // New state for our test component
  const [selectedSpace, setSelectedSpace] = useState('personal');

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  const toggleTestComponent = () => {
    setShowTestComponent(!showTestComponent);
  };

  const handlePeriodChange = (event) => {
    setSelectedPeriod(event.target.value);
    // The hook will automatically refresh when selectedPeriod changes
  };

  const handleDurationFilterChange = (filter) => {
    setDurationFilter(filter);
  };

  // Get the appropriate duration value based on the selected filter
  const getFilteredDuration = () => {
    if (!sessionStats) return 0;
    
    switch(durationFilter) {
      case 'active':
        return sessionStats.averageActivityTime || 0;
      case 'inactive':
        return sessionStats.averageInactivityTime || 0;
      case 'total':
      default:
        return sessionStats.averageDuration || 0;
    }
  };

  // Get the appropriate title based on the selected filter
  const getDurationTitle = () => {
    switch(durationFilter) {
      case 'active':
        return "Temps d'activité moyen";
      case 'inactive':
        return "Temps d'inactivité moyen";
      case 'total':
      default:
        return "Durée moyenne des sessions";
    }
  };

  // Get total time based on the selected filter
  const getFilteredTotalTime = () => {
    if (!sessionStats) return 0;
    
    switch(durationFilter) {
      case 'active':
        return sessionStats.totalActivityTime || 0;
      case 'inactive':
        return sessionStats.totalInactivityTime || 0;
      case 'total':
      default:
        return sessionStats.totalTime || 0;
    }
  };

  // Get times array based on the selected filter
  const getFilteredTimesArray = () => {
    if (!sessionStats) return [];
    
    switch(durationFilter) {
      case 'active':
        return sessionStats.activityTimes || [];
      case 'inactive':
        return sessionStats.inactivityTimes || [];
      case 'total':
      default:
        return sessionStats.sessionLengths || [];
    }
  };

  return (
    <div className="analytics-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <h1 className="dashboard-title">Dashboard Admin</h1>
        <button 
          onClick={toggleTestComponent}
          style={{
            marginLeft: '20px',
            padding: '8px 16px',
            backgroundColor: showTestComponent ? '#28a745' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          {showTestComponent ? '📊 Voir Dashboard' : '🧪 Test Backend'}
        </button>
      </div>

      {/* Show either the test component or the normal dashboard */}
      {showTestComponent ? (
        <SessionTestComponent />
      ) : (
        <>
          {/* Filter Section - Matching the design */}
          <div className="filter-container">
        <div className="filter-card">
          <div className="filter-group">
            <div className="filter-item">
              <FaSlidersH className="filter-icon" />
              <span className="filter-label">Période</span>
              <select
                className="filter-dropdown"
                value={selectedPeriod}
                onChange={handlePeriodChange}
              >
                <option value="Aujourd'hui">Aujourd'hui</option>
                <option value="7 derniers jours">7 derniers jours</option>
                <option value="30 derniers jours">30 derniers jours</option>
                <option value="3 derniers mois">3 derniers mois</option>
                <option value="1 dernière année">1 dernière année</option>
              </select>
            </div>
          </div>
          
          <div className="filter-actions">
            <button className="filter-button secondary">Tous les utilisateurs</button>
            <div className="search-container">
              <input 
                type="text" 
                placeholder="Chercher un utilisateur" 
                className="search-input"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Section Durée des Sessions - Focus KPI */}
      <div className="dashboard-section">
        <div className="number-card">
          {/* Title now stands alone at the top */}
          <h3 className="number-card-title">{getDurationTitle()}</h3>
          
          {/* Filter is now below the title */}
          <div className="card-filter">
            <div className="card-filter-options">
              <button 
                className={`card-filter-option ${durationFilter === 'total' ? 'active' : ''}`}
                onClick={() => handleDurationFilterChange('total')}
              >
                Total
              </button>
              <button 
                className={`card-filter-option ${durationFilter === 'active' ? 'active' : ''}`}
                onClick={() => handleDurationFilterChange('active')}
              >
                Activité
              </button>
              <button 
                className={`card-filter-option ${durationFilter === 'inactive' ? 'active' : ''}`}
                onClick={() => handleDurationFilterChange('inactive')}
              >
                Inactivité
              </button>
            </div>
          </div>

          {/* Card Value */}
          {!loading && !error && sessionStats ? (
            <div className="number-card-value">
              {formatDuration(getFilteredDuration())}
              <FaInfoCircle
                className="details-icon"
                onClick={toggleDetails}
                title="Voir les détails"
              />
            </div>
          ) : (
            <div className="number-card-loading">Chargement...</div>
          )}

          {/* Détails des calculs */}
          {showDetails && !loading && !error && sessionStats && (
            <div className="calculation-details">
              <h4>Détails du calcul pour: {selectedPeriod}</h4>
              <p>Nombre total de sessions: {sessionStats.totalSessions}</p>
              <p>Temps total: {formatDuration(getFilteredTotalTime())}</p>
              {durationFilter === 'total' && (
                <>
                  <p>Session la plus courte: {formatDuration(sessionStats.shortestSession)}</p>
                  <p>Session la plus longue: {formatDuration(sessionStats.longestSession)}</p>
                </>
              )}
              <h5>Durées individuelles (minutes):</h5>
              <ul>
                {getFilteredTimesArray().map((length, index) => (
                  <li key={index}>{formatDuration(length)}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
      
      {/* Usage Calendar Heatmap */}
      <UsageCalendarHeatmap />
      
      {/* Session Evolution Chart - Placé après le heatmap */}
      <SessionEvolutionChart selectedPeriod={selectedPeriod} />

      {/* Tool Usage Chart - Newly integrated chart component */}
      <ToolUsageChart selectedSpace={selectedSpace} selectedPeriod={selectedPeriod} onSpaceChange={setSelectedSpace} />

      {/* Add the ProjectsKPI component to the dashboard */}
      <div className="dashboard-section">
        <ProjectsKPI selectedPeriod={selectedPeriod} />
      </div>
        </>
      )}    
    </div>
  );
};

// Fonction pour formater la durée en heures et minutes
function formatDuration(minutes) {
  if (!minutes) return '0min';

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours === 0) {
    return `${mins}min`;
  } else if (mins === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${mins}min`;
  }
}

export default AdminAnalyticsDashboard;